import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import Modal from "../../components/UI/Modal";
import ReusableTable from "../../components/UI/ReusableTable";
import "react-loading-skeleton/dist/skeleton.css";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import Drawer from "../../components/UI/Drawer";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  MoreVertical,
  Calendar,
  Users,
  MapPin,
  Clock,
  User,
  FileText,
  Target,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  Building,
  Briefcase,
  DollarSign,
  GraduationCap,
  Star,
  Download,
  Phone,
  MapPin as LocationIcon,
} from "lucide-react";
import DOMPurify from "dompurify";
import { FaRupeeSign } from "react-icons/fa";
import StatusBadge from "../../components/UI/StatusBadge";

const HODRequests = React.memo(() => {
  const { user, roles } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedHODRequest, setSelectedHODRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedRequestForAction, setSelectedRequestForAction] =
    useState(null);
  const [actionType, setActionType] = useState("");
  const [feedback, setFeedback] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef({});
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // API base URL and headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("authToken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  // Fetch HOD requests
  // API response: { success, message, hodRequests: { data: [...departments], current_page, last_page, per_page, total, ... } }
  const fetchHODRequests = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://hrmspayroll.prepstripe.com/api/hod-requests?page=${page}`,
        { headers: getAuthHeaders() }
      );

      const hodRequests = response.data?.hodRequests;
      const departmentList = hodRequests?.data;

      if (response.data && Array.isArray(departmentList)) {
        const allRequests = [];
        departmentList.forEach((department) => {
          if (department.requests && Array.isArray(department.requests)) {
            department.requests.forEach((request) => {
              allRequests.push({
                ...request,
                department_name: department.department_name,
                department_id: department.department_id,
              });
            });
          }
        });

        setRequests(allRequests);
        setPagination({
          currentPage: hodRequests.current_page ?? page,
          totalPages: hodRequests.last_page ?? 1,
          totalItems: hodRequests.total ?? allRequests.length,
          perPage: hodRequests.per_page ?? 15,
        });
      } else {
        setRequests([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          perPage: 15,
        });
      }
    } catch (error) {
      console.error("Error fetching HOD requests:", error);
      setNotification({
        message:
          error?.response?.data?.message || "Failed to fetch HOD requests",
        type: "error",
      });
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete HOD request
  const deleteHODRequest = async (id) => {
    try {
      await axios.delete(
        `https://hrmspayroll.prepstripe.com/api-requests/${id}`,
        {
          headers: getAuthHeaders(),
        }
      );
      setNotification({
        message: "HOD request deleted successfully",
        type: "success",
      });
      fetchHODRequests(currentPage);
    } catch (error) {
      console.error("Error deleting HOD request:", error);
      setNotification({
        message:
          error?.response?.data?.message || "Failed to delete HOD request",
        type: "error",
      });
    }
  };

  // Action HOD request (Approve/Reject)
  const actionHODRequest = async (id, action, feedback) => {
    try {
      setActionLoading(true);
      const response = await axios.post(
        `https://hrmspayroll.prepstripe.com/api/hod-requests-action/${id}`,
        {
          action: action,
          feedback: feedback,
        },
        { headers: getAuthHeaders() }
      );

      if (response.data && response.data.success) {
        setNotification({
          message: `Request ${action.toLowerCase()} successfully`,
          type: "success",
        });
        closeActionModal();
        fetchHODRequests(currentPage);
      }
    } catch (error) {
      console.error("Error actioning HOD request:", error);
      setNotification({
        message:
          error?.response?.data?.message ||
          `Failed to ${action.toLowerCase()} request`,
        type: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Initialize data
  useEffect(() => {
    fetchHODRequests(1);
  }, []);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openDrawerForDetails = (hodRequest) => {
    setSelectedHODRequest(hodRequest);
    setIsDrawerOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleDeleteHODRequest = async (id) => {
    try {
      await deleteHODRequest(id);
    } catch (error) {
      setNotification({
        message: "Failed to delete HOD request",
        type: "error",
      });
    }
  };

  // Dropdown management
  const handleClickOutside = (event) => {
    if (!event.target.closest(".dropdown-container")) {
      setOpenDropdownId(null);
    }
  };

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();

    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);

      // Calculate portal coordinates
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();

      // Calculate dropdown position
      const dropdownWidth = 200; // min-w-[200px]
      const dropdownHeight = 150; // estimated height

      // Position dropdown to the right of the button
      let x = rect.right - dropdownWidth;
      let y = rect.bottom + 4; // 4px gap

      // Check if dropdown would go off-screen to the right
      if (x < 0) {
        x = rect.left - dropdownWidth;
      }

      // Check if dropdown would go off-screen to the bottom
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4; // Position above
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }

      // Ensure dropdown doesn't go off-screen to the left
      if (x < 0) {
        x = 8;
      }

      // Ensure dropdown doesn't go off-screen to the top
      if (y < 0) {
        y = 8;
      }

      setDropdownCoords({ x, y });
      // console.log('Portal coordinates:', { x, y, position: dropdownPosition });
    }
  };

  // Add click outside listener
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Memoized Columns Definition
  const columns = useMemo(
    () => [
      {
        label: "Sr. No.",
        field: "serial_number",
        type: "text",
        render: (row, index) => {
          return (currentPage - 1) * pagination.perPage + index + 1;
        },
      },
      {
        label: "Request Date",
        field: "created_at",
        type: "date",
        render: (row) => {
          if (!row.created_at) return "-";
          const date = new Date(row.created_at);
          const day = date.getDate().toString().padStart(2, "0");
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        },
      },
      {
        label: "Requester",
        field: "requester_name",
        type: "text",
        render: (row) => (
          <button
            className="text-blue-500 hover:text-blue-700 hover:underline hover:underline-offset-4"
            onClick={() => {
              setSelectedHODRequest(row);
              setIsDrawerOpen(true);
            }}
          >
            {row.requester_name || "-"}
          </button>
        ),
      },
      { label: "Department", field: "department_name", type: "text" },
      { label: "Designation", field: "designation", type: "text" },
      {
        label: "Status",
        field: "recruitment_request_status",
        type: "text",
        render: (row) => (
          <StatusBadge status={row?.recruitment_request_status} />
        ),
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                ref={(el) => {
                  if (el) buttonRefs.current[row.id] = el;
                }}
                onClick={(e) => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        ),
      },
    ],
    [roles, openDropdownId, dropdownPosition, currentPage, pagination.perPage]
  );

  // Derived filtered requests (search + status)
  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return requests.filter((r) => {
      // status filter
      if (
        statusFilter &&
        String(r.recruitment_request_status || "").toLowerCase() !==
          statusFilter.toLowerCase()
      ) {
        return false;
      }

      if (!q) return true;

      // search across requester, designation, department
      const fields = [
        r.requester_name,
        r.designation,
        r.department_name,
        r.requester_official_email,
      ];
      return fields.some((f) =>
        String(f || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [requests, searchQuery, statusFilter]);

  const availableStatuses = useMemo(() => {
    const set = new Set();
    requests.forEach((r) => {
      if (r.recruitment_request_status)
        set.add(String(r.recruitment_request_status));
    });
    return Array.from(set);
  }, [requests]);

  // Manual status options (use this to populate the status filter)
  const statusOptions = [
    "Pending",
    "Approved",
    "Rejected",
    "On Hold",
    "Cancelled",
    "Campaign Created",
  ];

  const filteredPageCount = Math.max(
    1,
    Math.ceil(filteredRequests.length / pagination.perPage)
  );

  const openModalForAdd = () => {
    setEditingRequest(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRequest(null);
  };

  const openActionModal = (request, action) => {
    setSelectedRequestForAction(request);
    setActionType(action);
    setFeedback("");
    setIsActionModalOpen(true);
  };

  const closeActionModal = () => {
    setIsActionModalOpen(false);
    setSelectedRequestForAction(null);
    setActionType("");
    setFeedback("");
  };

  const handleActionSubmit = (e) => {
    e.preventDefault();
    if (selectedRequestForAction && actionType) {
      actionHODRequest(selectedRequestForAction.id, actionType, feedback);
    }
  };

  // Memoized Exclude Filter Columns
  const excludeFilterColumns = useMemo(
    () => ["actions", "job_description"],
    []
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchHODRequests(newPage);
  };

  const getActionTitle = (action) => {
    const titleMap = {
      Approved: "Approve",
      Rejected: "Reject",
      "On Hold": "On Hold",
    };
    return titleMap[action] || action;
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-950 font-poppins rounded-lg">
      {/* notification component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          HOD Requests
        </h2>
        {roles.includes("Manager") && (
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
          >
            Add HOD Request
          </button>
        )}
      </div>

      {/* data table */}
      <div>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 w-full sm:w-1/2">
            <div className="relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by requester, designation, department..."
                className="w-full pr-10 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            >
              <option value="">All Statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {statusFilter && (
              <button
                onClick={() => setStatusFilter("")}
                className="px-3 py-2 bg-gray-100 rounded-md text-sm hover:bg-gray-200"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <ReusableTable
          data={filteredRequests}
          loading={loading}
          columns={columns}
          filterable={false}
        />

        {/* Pagination */}
        {!isFiltering && filteredRequests?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName="opacity-50 cursor-not-allowed"
            disabledLinkClassName="pointer-events-none"
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
            nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 border rounded dark:text-white font-poppins"
            pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
          />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingRequest ? "Edit HOD Request" : "Add New HOD Request"}
        fullSize={true}
      >
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-400">
            HOD Request form will be implemented here.
          </p>
        </div>
      </Modal>

      {/* Modal for Action (Approve/Reject) */}
      <Modal
        isOpen={isActionModalOpen}
        onRequestClose={closeActionModal}
        title={`${getActionTitle(actionType)} Request`}
        fullSize={false}
      >
        <div className="">
          {selectedRequestForAction && (
            <form onSubmit={handleActionSubmit}>
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Request Details:
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Requester:</span>{" "}
                      {selectedRequestForAction.requester_name}
                    </div>
                    <div>
                      <span className="font-medium">Department:</span>{" "}
                      {selectedRequestForAction.department_name}
                    </div>
                    <div>
                      <span className="font-medium">Designation:</span>{" "}
                      {selectedRequestForAction.designation}
                    </div>
                    <div>
                      <span className="font-medium">Positions:</span>{" "}
                      {selectedRequestForAction.number_of_positions}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feedback{" "}
                    {actionType === "Rejected" ? (
                      <span className="text-red-500">*</span>
                    ) : (
                      <span className="text-gray-500">(Optional)</span>
                    )}
                  </label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    rows={4}
                    required={actionType === "Rejected"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    placeholder={`Enter feedback for ${actionType.toLowerCase()} this request...`}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeActionModal}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className={`px-4 py-2 text-white rounded-md hover:opacity-90 disabled:opacity-50 ${
                    actionType === "Approved"
                      ? "bg-green-600 hover:bg-green-700"
                      : actionType === "On Hold"
                      ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                      : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {actionLoading
                    ? "Processing..."
                    : `${getActionTitle(actionType)} Request`}
                </button>
              </div>
            </form>
          )}
        </div>
      </Modal>

      {/* Drawer for HOD Request Details */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        border_radius="md:rounded-none"
        showClose={false}
        ChilDiv=""
        ChildParent=""
      >
        <div className="mx-auto bg-white shadow-2xl overflow-hidden h-full">
          {/* Header Section */}
          <div className="bg-blue-500 px-8 py-8 text-white font-poppins">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-semibold mb-2">
                  {selectedHODRequest?.designation ||
                    selectedHODRequest?.position ||
                    "HOD Request Details"}
                </h1>
                <div className="flex items-center space-x-6 text-blue-100">
                  <div className="flex items-center space-x-2">
                    <Building className="h-5 w-5" />
                    <span>
                      {selectedHODRequest?.department_name ||
                        selectedHODRequest?.department}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <LocationIcon className="h-5 w-5" />
                    <span>{selectedHODRequest?.location}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={closeDrawer}
                className="text-white dark:hover:text-gray-400 hover:bg-blue-800 hover:text-gray-200 text-xl px-2 py-1 rounded-md transition delay-100"
              >
                ✖
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8 font-poppins overflow-y-auto max-h-[calc(100vh-200px)]">
            {selectedHODRequest ? (
              <>
                {/* Key Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-blue-500 p-2 rounded-lg">
                        <Users className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-blue-600 font-medium">
                        Positions
                      </span>
                    </div>
                    <p className="text-2xl font-semibold text-gray-900">
                      {selectedHODRequest.number_of_positions}
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 border border-green-100">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-green-500 p-2 rounded-lg">
                        <Briefcase className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-green-600 font-medium">
                        Employment
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedHODRequest.employment_type}
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 border border-purple-100">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-purple-500 p-2 rounded-lg">
                        <Target className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-purple-600 font-medium">
                        Hiring Type
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedHODRequest.hiring_type}
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 border border-orange-100">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="bg-orange-500 p-2 rounded-lg">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-orange-600 font-medium">
                        Requester
                      </span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedHODRequest.requester_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedHODRequest.requester_official_email}
                    </p>
                  </div>
                </div>

                {/* Recruitment Request Status */}
                {selectedHODRequest.recruitment_request_status && (
                  <div className="mb-8 font-poppins">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <CheckCircle className="h-6 w-6 text-blue-600" />
                      <span>Status</span>
                    </h3>
                    <div className="flex items-center">
                      <StatusBadge
                        status={selectedHODRequest.recruitment_request_status}
                      />
                    </div>
                  </div>
                )}

                {/* Skills Section */}
                {selectedHODRequest.skill_set && (
                  <div className="mb-8 font-poppins">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Target className="h-6 w-6 text-blue-600" />
                      <span>Required Skills</span>
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {selectedHODRequest.skill_set
                        .split(/\r\n|\n|,/)
                        .map((skill) => skill.trim())
                        .filter((skill) => skill.length > 0)
                        .map((skill, index) => (
                          <span
                            key={index}
                            className="bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium border border-blue-100 transition-shadow duration-200"
                          >
                            {skill}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {/* Requester Information Section */}
                {/* <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <User className="h-6 w-6 text-blue-600" />
                    <span>Requester Information</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                                <User className="h-5 w-5 text-blue-600" />
                                <span className="font-medium text-gray-900">Requester Name</span>
                      </div>
                            <p className="text-gray-700">{selectedHODRequest.requester_name}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                                <Mail className="h-5 w-5 text-blue-600" />
                                <span className="font-medium text-gray-900">Official Email</span>
                      </div>
                            <p className="text-gray-700">{selectedHODRequest.requester_official_email}</p>
                    </div>
                  </div>
                </div> */}

                {/* Position & Employment Details */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Briefcase className="h-6 w-6 text-green-600" />
                    <span>Position & Employment Details</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <FaRupeeSign className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-gray-900">
                          Stipend/CTC per Month
                        </span>
                      </div>
                      <p className="text-gray-700">
                        {selectedHODRequest.stipend_ctc_per_month ||
                          "Not specified"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Job Description */}
                {selectedHODRequest.job_description &&
                  typeof selectedHODRequest.job_description === "string" &&
                  !selectedHODRequest.job_description_url && (
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                        <FileText className="h-6 w-6 text-green-600" />
                        <span>Job Description</span>
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                        <div
                          className="text-gray-700 leading-relaxed overflow-hidden"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              selectedHODRequest.job_description,
                              {
                                ALLOWED_TAGS: [
                                  "p",
                                  "br",
                                  "strong",
                                  "em",
                                  "u",
                                  "h1",
                                  "h2",
                                  "h3",
                                  "h4",
                                  "h5",
                                  "h6",
                                  "ul",
                                  "ol",
                                  "li",
                                  "blockquote",
                                  "code",
                                  "pre",
                                  "span",
                                  "div",
                                ],
                                ALLOWED_ATTR: ["class", "style"],
                              }
                            ),
                          }}
                        />
                      </div>
                    </div>
                  )}

                {/* Requirements & Skills */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <GraduationCap className="h-6 w-6 text-orange-600" />
                    <span>Requirements & Skills</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <GraduationCap className="h-5 w-5 text-orange-600" />
                        <span className="font-medium text-gray-900">
                          Minimum Qualification
                        </span>
                      </div>
                      <p className="text-gray-700">
                        {selectedHODRequest.minimum_qualification ||
                          "Not specified"}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <Clock className="h-5 w-5 text-orange-600" />
                        <span className="font-medium text-gray-900">
                          Years Experience
                        </span>
                      </div>
                      <p className="text-gray-700">
                        {selectedHODRequest.years_experience_required
                          ? `${
                              selectedHODRequest.years_experience_required
                            } year${
                              selectedHODRequest.years_experience_required === 1
                                ? ""
                                : "s"
                            }`
                          : "Not specified"}
                      </p>
                    </div>
                    {/* <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <User className="h-5 w-5 text-orange-600" />
                                <span className="font-medium text-gray-900">Reporting To</span>
                            </div>
                            <p className="text-gray-700">{selectedHODRequest.reporting_to || 'Not specified'}</p>
                        </div> */}
                  </div>
                </div>

                {/* Interview & Process Details */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                    <Users className="h-6 w-6 text-indigo-600" />
                    <span>Interview & Process Details</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <Clock className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium text-gray-900">
                          TAT Days to Close
                        </span>
                      </div>
                      <p className="text-gray-700">
                        {selectedHODRequest.tat_days_to_close ||
                          "Not specified"}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center space-x-3 mb-3">
                        <Users className="h-5 w-5 text-indigo-600" />
                        <span className="font-medium text-gray-900">
                          Interview Rounds
                        </span>
                      </div>
                      <p className="text-gray-700">
                        {selectedHODRequest.interview_rounds}
                      </p>
                    </div>
                  </div>

                  {/* Interview Steps */}
                  {selectedHODRequest.round_type_mapping &&
                    Array.isArray(selectedHODRequest.round_type_mapping) &&
                    selectedHODRequest.round_type_mapping.length > 0 && (
                      <div className="mt-6">
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-4">
                            <FileText className="h-5 w-5 text-indigo-600" />
                            <span className="font-medium text-gray-900">
                              Interview Steps
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {selectedHODRequest.round_type_mapping.map(
                              (roundType, index) => (
                                <span
                                  key={index}
                                  className="bg-indigo-50 text-indigo-600 px-4 py-2 rounded-full text-sm font-medium border border-indigo-100 transition-shadow duration-200"
                                >
                                  {index + 1}. {roundType}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* {selectedHODRequest.interview_process_steps && (
                    <div className="mt-6">
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 mb-3">
                                <FileText className="h-5 w-5 text-indigo-600" />
                                <span className="font-medium text-gray-900">Interview Process Steps</span>
                            </div>
                            <p className="text-gray-700 whitespace-pre-wrap">{selectedHODRequest.interview_process_steps}</p>
                        </div>
                    </div>
                    )} */}
                </div>

                {/* Additional Notes & Suggestions */}
                {(selectedHODRequest.other_notes ||
                  selectedHODRequest.additional_suggestions) && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <FileText className="h-6 w-6 text-yellow-600" />
                      <span>Additional Notes & Suggestions</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedHODRequest.other_notes && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <FileText className="h-5 w-5 text-yellow-600" />
                            <span className="font-medium text-gray-900">
                              Other Notes
                            </span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {selectedHODRequest.other_notes}
                          </p>
                        </div>
                      )}
                      {selectedHODRequest.additional_suggestions && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <FileText className="h-5 w-5 text-yellow-600" />
                            <span className="font-medium text-gray-900">
                              Additional Suggestions
                            </span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {selectedHODRequest.additional_suggestions}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* File Attachments */}
                {(selectedHODRequest.approval_attachment_mail_url ||
                  selectedHODRequest.job_description_url ||
                  selectedHODRequest.kra_kpi_url ||
                  selectedHODRequest.technical_test_path_url) && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <Download className="h-6 w-6 text-red-600" />
                      <spa n>File Attachments</spa>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {selectedHODRequest.approval_attachment_mail_url && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <Download className="h-5 w-5 text-red-600" />
                            <span className="font-medium text-gray-900">
                              Approval Attachment
                            </span>
                          </div>
                          <a
                            href={
                              selectedHODRequest.approval_attachment_mail_url
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            View File
                          </a>
                        </div>
                      )}
                      {selectedHODRequest.job_description_url && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <Download className="h-5 w-5 text-red-600" />
                            <span className="font-medium text-gray-900">
                              Job Description
                            </span>
                          </div>
                          <a
                            href={selectedHODRequest.job_description_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            View File
                          </a>
                        </div>
                      )}
                      {selectedHODRequest.kra_kpi_url && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <Download className="h-5 w-5 text-red-600" />
                            <span className="font-medium text-gray-900">
                              KRA/KPI
                            </span>
                          </div>
                          <a
                            href={selectedHODRequest.kra_kpi_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            View File
                          </a>
                        </div>
                      )}
                      {selectedHODRequest.technical_test_path_url && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <Download className="h-5 w-5 text-red-600" />
                            <span className="font-medium text-gray-900">
                              Technical Test
                            </span>
                          </div>
                          <a
                            href={selectedHODRequest.technical_test_path_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4 flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            View File
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Information */}
                {(selectedHODRequest.actioned_by ||
                  selectedHODRequest.actioned_at) && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <svg
                        className="h-6 w-6 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <span>Action Information</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {selectedHODRequest.actioned_by && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <svg
                              className="h-5 w-5 text-purple-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              ></path>
                            </svg>
                            <span className="font-medium text-gray-900">
                              Actioned By
                            </span>
                          </div>
                          <p className="text-gray-700">
                            {selectedHODRequest.actioned_by}
                          </p>
                        </div>
                      )}
                      {selectedHODRequest.actioned_at && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                          <div className="flex items-center space-x-3 mb-3">
                            <svg
                              className="h-5 w-5 text-purple-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              ></path>
                            </svg>
                            <span className="font-medium text-gray-900">
                              Actioned At
                            </span>
                          </div>
                          <p className="text-gray-700">
                            {new Date(
                              selectedHODRequest.actioned_at
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {selectedHODRequest.actioner_remarks && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 md:col-span-2 lg:col-span-3">
                          <div className="flex items-center space-x-3 mb-3">
                            <svg
                              className="h-5 w-5 text-purple-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              ></path>
                            </svg>
                            <span className="font-medium text-gray-900">
                              Remarks
                            </span>
                          </div>
                          <p className="text-gray-700 whitespace-pre-wrap">
                            {selectedHODRequest.actioner_remarks}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons - Bottom Right */}
                {roles.includes("HOD") &&
                  (selectedHODRequest?.recruitment_request_status ===
                    "Pending" ||
                    selectedHODRequest?.recruitment_request_status ===
                      "On Hold") && (
                    <div className="mb-8 flex justify-end">
                      <div className="flex flex-wrap gap-3">
                        {/* Approve */}
                        <button
                          onClick={() =>
                            openActionModal(selectedHODRequest, "Approved")
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Approve
                        </button>

                        {/* Reject */}
                        <button
                          onClick={() =>
                            openActionModal(selectedHODRequest, "Rejected")
                          }
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                          <XCircle className="h-4 w-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  )}

                {/* Timeline Footer */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span>
                        Created: {formatDate(selectedHODRequest.created_at)}
                      </span>
                      <span>•</span>
                      <span>
                        Updated: {formatDate(selectedHODRequest.updated_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500 text-lg">
                    No HOD request selected
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Drawer>

      {/* Portal Dropdown */}
      {openDropdownId &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: dropdownCoords.y,
              left: dropdownCoords.x,
              zIndex: 9999,
            }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[200px]"
          >
            <div className="py-1 flex flex-col space-y-0">
              {/* View Details */}
              <button
                onClick={() => {
                  const row = requests.find((r) => r.id === openDropdownId);
                  if (row) {
                    openDrawerForDetails(row);
                  }
                  setOpenDropdownId(null);
                }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
              >
                View Details
              </button>

              {/* Approval Actions for Pending */}
              {(() => {
                const row = requests.find((r) => r.id === openDropdownId);
                return roles.includes("HOD") &&
                  (row?.recruitment_request_status === "Pending" ||
                    row?.recruitment_request_status === "On Hold") ? (
                  <>
                    <button
                      onClick={() => {
                        if (row) {
                          openActionModal(row, "Approved");
                        }
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors block"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        if (row) {
                          openActionModal(row, "Rejected");
                        }
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors block"
                    >
                      Reject
                    </button>
                  </>
                ) : null;
              })()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
});

export default HODRequests;
